import React from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "reactstrap";
import {
  WORKING_FILL,
  WORKING_STROKE,
  VARIANT_FILL,
  VARIANT_STROKE,
  NEUTRAL_FILL,
  NEUTRAL_STROKE,
  LINE,
  LABEL,
  wrapLabel,
  DiagramFigure,
  Legend,
  LegendItem,
} from "./diagramTheme";

const VIEW_W = 440;
const VIEW_H = 250;
const PARENT_R = 27;
const CHILD_R = 25;
const PARENT_CY = 52;
const PARENT_X = [155, 285];
const COUPLE_MID_X = 220;
const COUPLE_DROP_Y = 96;
const CHILD_CY = 151;
const CHILD_X = [62, 168, 274, 380];
const BRACKET_Y = 188;

// A person carries two copies. `gene` is an autosomal copy; `X`/`Y` are the sex
// chromosomes, drawn with their letter so the panel reads without colour.
const gene = (variant) => ({ kind: "gene", variant });
const X = (variant) => ({ kind: "X", variant });
const Y = () => ({ kind: "Y", variant: false });

const CARRIER = [gene(true), gene(false)];
const AFFECTED = [gene(true), gene(true)];
const NON_CARRIER = [gene(false), gene(false)];

// One variant copy is enough for an autosomal dominant condition, so the same
// single-amber-pill person that reads as an unaffected carrier in the recessive
// row is an affected person here. The outcome labels and the heavier ring carry
// that difference, which is why neither can be dropped in favour of colour.
const DOMINANT_AFFECTED = [gene(true), gene(false)];

// Rows mirror the headings in the service copy: autosomal recessive, X-linked,
// and autosomal dominant. `rows` on the component picks which of these a given
// section shows, so a page can carry one pattern or several.
const ROWS = [
  {
    key: "autosomal",
    scenarios: [
      {
        key: "bothCarriers",
        parents: [CARRIER, CARRIER],
        children: [AFFECTED, CARRIER, CARRIER, NON_CARRIER],
        affected: [0],
        groups: [
          { from: 0, to: 0, label: "affected", percent: "25%" },
          { from: 1, to: 2, label: "carrier", percent: "50%" },
          { from: 3, to: 3, label: "unaffected", percent: "25%" },
        ],
      },
      {
        key: "oneCarrier",
        parents: [CARRIER, NON_CARRIER],
        children: [CARRIER, CARRIER, NON_CARRIER, NON_CARRIER],
        affected: [],
        groups: [
          { from: 0, to: 1, label: "carrier", percent: "50%" },
          { from: 2, to: 3, label: "unaffected", percent: "50%" },
        ],
      },
    ],
  },
  {
    key: "xlinked",
    scenarios: [
      {
        key: "xlinkedCarrier",
        // Children are ordered two-X first, then one-X-and-one-Y, so the
        // labels beneath run in the same order as the copy explains them.
        parents: [
          [X(true), X(false)],
          [X(false), Y()],
        ],
        children: [
          [X(true), X(false)],
          [X(false), X(false)],
          [X(true), Y()],
          [X(false), Y()],
        ],
        affected: [2],
        groups: [
          { from: 0, to: 0, label: "twoXCarrier", percent: "25%" },
          { from: 1, to: 1, label: "twoXNotCarrier", percent: "25%" },
          { from: 2, to: 2, label: "oneXAffected", percent: "25%" },
          { from: 3, to: 3, label: "oneXNotAffected", percent: "25%" },
        ],
      },
    ],
  },
  {
    key: "dominant",
    scenarios: [
      {
        key: "dominantParent",
        parents: [DOMINANT_AFFECTED, NON_CARRIER],
        children: [
          DOMINANT_AFFECTED,
          DOMINANT_AFFECTED,
          NON_CARRIER,
          NON_CARRIER,
        ],
        affected: [0, 1],
        groups: [
          { from: 0, to: 1, label: "affected", percent: "50%" },
          { from: 2, to: 3, label: "unaffected", percent: "50%" },
        ],
      },
    ],
  },
];

const ALL_ROWS = ROWS.map((row) => row.key);

const Copy = ({ cx, cy, copy }) => {
  const isY = copy.kind === "Y";
  const isSexChromosome = copy.kind !== "gene";
  const width = isSexChromosome ? 10 : 8;
  const height = isY ? 18 : 26;
  return (
    <g>
      <rect
        x={cx - width / 2}
        y={cy - height / 2}
        width={width}
        height={height}
        rx={4}
        fill={copy.variant ? VARIANT_FILL : WORKING_FILL}
        stroke={copy.variant ? VARIANT_STROKE : WORKING_STROKE}
        strokeWidth={1.2}
      />
      {isSexChromosome && (
        <text
          x={cx}
          y={cy + 3.5}
          textAnchor="middle"
          fontSize={9}
          fontWeight="bold"
          fill={copy.variant ? "#5A4200" : "#2A6360"}
        >
          {copy.kind}
        </text>
      )}
    </g>
  );
};

const Person = ({ cx, cy, r, copies, isAffected }) => {
  const isSexChromosome = copies[0].kind !== "gene";
  const gap = isSexChromosome ? 6 : 5;
  const half = isSexChromosome ? 5 : 4;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={NEUTRAL_FILL}
        stroke={isAffected ? VARIANT_STROKE : NEUTRAL_STROKE}
        strokeWidth={isAffected ? 2.5 : 1.5}
      />
      <Copy cx={cx - half - gap / 2} cy={cy} copy={copies[0]} />
      <Copy cx={cx + half + gap / 2} cy={cy} copy={copies[1]} />
    </g>
  );
};

const OutcomeGroup = ({ group, text }) => {
  const left = CHILD_X[group.from];
  const right = CHILD_X[group.to];
  const centre = (left + right) / 2;
  const lines = wrapLabel(text);
  return (
    <g>
      {group.from !== group.to && (
        <path
          d={`M ${left} ${BRACKET_Y - 6} L ${left} ${BRACKET_Y} L ${right} ${BRACKET_Y} L ${right} ${BRACKET_Y - 6}`}
          fill="none"
          stroke={LINE}
          strokeWidth={1.5}
        />
      )}
      {lines.map((line, i) => (
        <text
          key={line + i}
          x={centre}
          y={BRACKET_Y + 18 + i * 15}
          textAnchor="middle"
          fontSize={13}
          fill={LABEL}
        >
          {line}
        </text>
      ))}
      <text
        x={centre}
        y={BRACKET_Y + 20 + lines.length * 15}
        textAnchor="middle"
        fontSize={15}
        fontWeight="bold"
        fill={LABEL}
      >
        {group.percent}
      </text>
    </g>
  );
};

const Scenario = ({ scenario, t, base }) => {
  const key = `${base}.${scenario.key}`;
  const titleId = `inheritance-${scenario.key}-title`;
  const descId = `inheritance-${scenario.key}-desc`;
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      role="img"
      aria-labelledby={`${titleId} ${descId}`}
      style={{ maxWidth: "440px", height: "auto", overflow: "visible" }}
    >
      <title id={titleId}>{t(`${key}.heading`)}</title>
      <desc id={descId}>{t(`${key}.note`)}</desc>

      {PARENT_X.map((x, i) => (
        <text
          key={`plabel-${x}`}
          x={x}
          y={14}
          textAnchor="middle"
          fontSize={13}
          fill={LABEL}
        >
          {t(`${key}.${i === 0 ? "parentA" : "parentB"}`)}
        </text>
      ))}
      {PARENT_X.map((x, i) => (
        <Person
          key={`parent-${x}`}
          cx={x}
          cy={PARENT_CY}
          r={PARENT_R}
          copies={scenario.parents[i]}
          isAffected={false}
        />
      ))}

      {/* couple line, drop to the sibling bar, and one drop per child */}
      <path
        d={`M ${PARENT_X[0] + PARENT_R} ${PARENT_CY} L ${PARENT_X[1] - PARENT_R} ${PARENT_CY}`}
        stroke={LINE}
        strokeWidth={1.5}
      />
      <path
        d={`M ${COUPLE_MID_X} ${PARENT_CY} L ${COUPLE_MID_X} ${COUPLE_DROP_Y} M ${CHILD_X[0]} ${COUPLE_DROP_Y} L ${CHILD_X[3]} ${COUPLE_DROP_Y}`}
        stroke={LINE}
        strokeWidth={1.5}
        fill="none"
      />
      {CHILD_X.map((x) => (
        <path
          key={`drop-${x}`}
          d={`M ${x} ${COUPLE_DROP_Y} L ${x} ${CHILD_CY - CHILD_R}`}
          stroke={LINE}
          strokeWidth={1.5}
        />
      ))}

      {CHILD_X.map((x, i) => (
        <Person
          key={`child-${x}`}
          cx={x}
          cy={CHILD_CY}
          r={CHILD_R}
          copies={scenario.children[i]}
          isAffected={scenario.affected.includes(i)}
        />
      ))}

      {scenario.groups.map((group) => (
        <OutcomeGroup
          key={`${scenario.key}-${group.label}-${group.from}`}
          group={group}
          text={t(`${key}.${group.label}`)}
        />
      ))}
    </svg>
  );
};

const CopySwatch = ({ variant }) => (
  <svg width="12" height="26" aria-hidden="true" focusable="false">
    <rect
      x={1}
      y={1}
      width={8}
      height={24}
      rx={4}
      fill={variant ? VARIANT_FILL : WORKING_FILL}
      stroke={variant ? VARIANT_STROKE : WORKING_STROKE}
      strokeWidth={1.2}
    />
  </svg>
);

// `rows` defaults to the recessive and X-linked pair this component shipped
// with, so an existing caller that names no rows keeps the figure it had when
// the dominant row was added.
function InheritanceDiagram({
  serviceKey = "carrierScreening",
  rows = ["autosomal", "xlinked"],
  shellBase,
  bare = false,
}) {
  const { t } = useTranslation();
  // The scenario labels, the legend and the row headings are shared: a
  // recessive panel says the same thing wherever it is shown. Only the wrapper
  // copy, the caption and the screen-reader summary belong to the section that
  // is showing it, which is what `shellBase` overrides.
  const base = `landing.service.${serviceKey}.diagram`;
  const shown = ROWS.filter((row) => rows.includes(row.key));

  return (
    <DiagramFigure
      base={shellBase || base}
      t={t}
      bare={bare}
      legend={
        <Legend>
          <LegendItem swatch={<CopySwatch />}>
            {t(`${base}.legend.working`)}
          </LegendItem>
          <LegendItem swatch={<CopySwatch variant />}>
            {t(`${base}.legend.variant`)}
          </LegendItem>
        </Legend>
      }
    >
      {shown.map((row) => (
        <section key={row.key} style={{ marginBottom: "18px" }}>
          <h3
            style={{
              fontSize: "1.0625rem",
              fontWeight: 700,
              margin: "0 0 4px",
            }}
          >
            {t(`${base}.${row.key}.heading`)}
          </h3>
          {/* The row note explains the pattern the panels below illustrate.
              Inside a screening subsection the prose immediately above has just
              explained it, so repeating it here restates a whole paragraph a
              screen apart. The heading stays either way: it is short, and it is
              what groups the panels under the pattern they belong to. */}
          {!bare && (
            <p style={{ fontSize: "15px", color: "#666", margin: "0 0 14px" }}>
              {t(`${base}.${row.key}.note`)}
            </p>
          )}
          <Row>
            {row.scenarios.map((scenario) => (
              <Col key={scenario.key} md="6" className="text-center mb-4">
                <h4
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    marginBottom: "12px",
                    color: LABEL,
                  }}
                >
                  {t(`${base}.${scenario.key}.heading`)}
                </h4>
                <Scenario scenario={scenario} t={t} base={base} />
                <p
                  style={{
                    fontSize: "15px",
                    color: "#666",
                    marginTop: "14px",
                    textAlign: "left",
                  }}
                >
                  {t(`${base}.${scenario.key}.note`)}
                </p>
              </Col>
            ))}
          </Row>
        </section>
      ))}
    </DiagramFigure>
  );
}

// Exported so a test can assert every row's strings exist without restating the
// row list, which would then silently stop covering a row added later.
export { ALL_ROWS };
export default InheritanceDiagram;
